import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Form, Popconfirm } from 'antd';
import { useEffect, useState } from 'react';

import DrawerComponent from 'components/Drawer';
import UserDetailContent from './UserDetailContent';
import UserEditContent from './UserEditContent';

import useDeleteUser from 'screens/users/hooks/useDeleteUserMutation';

import { RizeUser } from 'types/common';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';

const HasFarmerGroupError = (
  <>
    This user cannot be deleted because they are still managing Farmer Groups.
    Please <b>reassign</b> or <b>remove</b> the Farmer Groups before trying
    again.
  </>
);

type EditUserDrawerProps = {
  country: string;
  data?: RizeUser;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const UserDrawer = ({
  country,
  data,
  open,
  onClose,
  onSuccess,
}: EditUserDrawerProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>();
  const [form] = Form.useForm();

  const handleClose = () => {
    if (!isEditing) {
      setErrorMessage(undefined);
      onClose();
      return;
    }
    setIsEditing(false);
  };

  const handleSuccess = () => {
    setIsEditing(false);
    onSuccess();
  };

  const handleDelete = () => {
    if (!data?.id) return;
    mutate(data.id);
  };

  const { mutate, isPending: isLoadingDelete } = useDeleteUser({
    onSuccess: () => {
      handleClose();
      onSuccess();
    },
    onError: (data) => {
      setErrorMessage(
        data.errorKey === 'hasFarmerGroup' ? HasFarmerGroupError : data.message,
      );
    },
  });

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  return (
    <DrawerComponent
      title={isEditing ? 'Edit User' : 'User Details'}
      open={open}
      onClose={handleClose}
      loading={isLoadingEdit || isLoadingDelete}
      extra={
        !isEditing && (
          <Flex gap={10}>
            <ProtectedResource permission="user:edit">
              <Button
                onClick={() => setIsEditing(true)}
                loading={isLoadingEdit}
                disabled={isLoadingDelete}
              >
                Edit
              </Button>
            </ProtectedResource>
            <ProtectedResource permission="user:remove">
              <Popconfirm
                title="Delete this user?"
                description="This action cannot be undone. Are you sure you want to delete this user?"
                okText="Delete User"
                overlayStyle={{ maxWidth: '320px' }}
                okButtonProps={{ danger: true, type: 'primary' }}
                onConfirm={handleDelete}
              >
                <Button danger loading={isLoadingDelete}>
                  Delete
                </Button>
              </Popconfirm>
            </ProtectedResource>
          </Flex>
        )
      }
      closeIcon={isEditing && <ArrowLeftOutlined />}
      content={
        isEditing ? (
          <UserEditContent
            data={data}
            onSuccess={handleSuccess}
            setLoading={setIsLoadingEdit}
          />
        ) : (
          <>
            {errorMessage && (
              <Alert
                message="Can’t delete user"
                description={errorMessage}
                type="warning"
                showIcon
                closable
                style={{ marginBottom: '20px' }}
              />
            )}
            <UserDetailContent data={data} country={country} />
          </>
        )
      }
    />
  );
};

export default UserDrawer;
